
import React, { useState, useEffect } from 'react';
import { TextField } from '@mui/material';
import {
  StyledDialog,
  StyledDialogTitle,
  StyledDialogContent,
  StyledDialogActions,
  CancelButton,
  SaveButton
} from './styles';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../../../../store';
import { createQualification, updateQualification } from '../../../../../modules/masterdata/actions';
import { IQualification } from '../../../../../modules/masterdata/types';
import { selectCreateQualificationSuccess, selectUpdateQualificationSuccess } from '../../../../../modules/masterdata/selectors';

interface QualificationModalProps {
  open: boolean;
  onClose: () => void;
  qualification?: IQualification | null; // If provided, we are in Edit mode
}

const QualificationModal: React.FC<QualificationModalProps> = ({ open, onClose, qualification }) => {
  const dispatch = useDispatch<AppDispatch>();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const isCreateSuccess = useSelector(selectCreateQualificationSuccess);
  const isUpdateSuccess = useSelector(selectUpdateQualificationSuccess);

  useEffect(() => {
    if (qualification) {
      setName(qualification.qualification_name);
      setDescription(qualification.qualification_description);
    } else {
      setName('');
      setDescription('');
    }
  }, [qualification, open]);

  useEffect(() => {
    if (isCreateSuccess) {
      onClose();
    }
    if (isUpdateSuccess) {
      onClose();
    }
  }, [isCreateSuccess, isUpdateSuccess, onClose]);


  const [nameError, setNameError] = useState(false);
  const [descriptionError, setDescriptionError] = useState(false);

  const handleSave = () => {
    const isNameValid = !!name.trim();
    const isDescriptionValid = !!description.trim();

    setNameError(!isNameValid);
    setDescriptionError(!isDescriptionValid);

    if (!isNameValid || !isDescriptionValid) {
      return;
    }

    if (qualification) {
      dispatch(updateQualification({
        id: qualification.qualification_id,
        qualification_name: name,
        qualification_description: description
      }));
    } else {
      dispatch(createQualification({
        qualification_name: name,
        qualification_description: description
      }));
    }
  };

  return (
    <StyledDialog open={open} onClose={onClose}>
      <StyledDialogTitle>
        {qualification ? 'Edit Qualification' : 'Create Qualification'}
      </StyledDialogTitle>
      <StyledDialogContent>
        <TextField
          autoFocus
          margin="dense"
          label="Name"
          required
          fullWidth
          value={name}
          error={nameError}
          helperText={nameError ? 'Name is required' : ''}
          onChange={(e) => {
            setName(e.target.value);
            if (e.target.value.trim()) setNameError(false);
          }}
        />
        <TextField
          margin="dense"
          label="Description"
          required
          fullWidth
          multiline
          rows={3}
          value={description}
          error={descriptionError}
          helperText={descriptionError ? 'Description is required' : ''}
          onChange={(e) => {
            setDescription(e.target.value);
            if (e.target.value.trim()) setDescriptionError(false);
          }}
        />
      </StyledDialogContent>
      <StyledDialogActions>
        <CancelButton onClick={onClose}>
          Cancel
        </CancelButton>
        <SaveButton onClick={handleSave} variant="contained" disableElevation>
          {qualification ? 'Save Changes' : 'Create'}
        </SaveButton>
      </StyledDialogActions>
    </StyledDialog>
  );
};

export default QualificationModal;
